// Full-document shell for tempurpedic pages rendered entirely through React.
// reactivated's renderer injects the bundle CSS/JS + __PRELOADED_PROPS__, so we
// deliberately emit none of that here.
//
// The header is the rich-nav SiteHeader (it renders the flash messages
// INTERNALLY -- so PageSkeleton must NOT render a separate <Messages>; the
// per-page pageChrome.messages are threaded into it below).
// legacy.js is loaded as an `async` <script> at body-end (see the comment there
// for why), and zendesk loads at body-end gated on the page's chat settings.
import { Immutable } from "immer";
import React from "react";
import { ErrorBoundary } from "tsi-common-react/src/components/ErrorBoundary/index";
import { MetaTags } from "tsi-common-react/src/components/MetaTags/index";
import {
    initPromoCalloutMessages,
    useFocusFirstFormError,
    useLinkRewrites,
    useRichTextDecorators,
} from "tsi-common-react/src/utils/richTextDecorators";
import { t } from "ttag";

import { Context, templates } from "@reactivated";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { Template as FinancingModal } from "../../templates/FinancingModalTemplate";
import { Template as SessionKeepAlive } from "../../templates/SessionKeepAliveTemplate";
import { rememberPageLanguage } from "../../utils/languageCookie";
import { Sale2SiteHeader } from "../Sale2SiteHeader";
import { SiteFooter } from "../SiteFooter";
import { SiteHeader } from "../SiteHeader";
import { IHeaderMessages, adaptHeaderMessages } from "./messages";

type ITPPageChrome = Immutable<templates.UtilityPageTemplate["page_chrome"]>;
// Fields only the tempurpedic PageChrome carries. Optional here so the shared
// tsicommon PageChrome (carried by the Track-B document templates) stays
// assignable to the `pageChrome` prop; its analogues (current_url /
// seo_heading) are read as fallbacks below. `messages` accepts BOTH the TP
// Message shape and the shared MessageItem shape; adaptHeaderMessages
// (./messages) converts to the TP shape the in-tree SiteHeader's <Messages>
// consumes.
type ITPOnlyField =
    | "hreflang_alternates"
    | "seo_title_bare"
    | "og_url"
    | "event_name"
    | "event_data"
    | "og_title"
    | "twitter_description";
type IPageChrome = Omit<ITPPageChrome, ITPOnlyField | "messages"> &
    Partial<Pick<ITPPageChrome, ITPOnlyField>> & {
        messages: IHeaderMessages;
        current_url?: string;
        seo_heading?: string;
    };

// Per-page chrome (title, meta, canonical, hreflang, JSON-LD, body_class,
// flash messages) is carried as the `page_chrome` field on each full-page
// @template. The global document shell (head/body globals, header, footer)
// comes from the `chrome` context processor (useContext below).
type IProps = {
    pageChrome: IPageChrome;
    children?: React.ReactNode;
    // Header/footer slots. A node renders in place of the default
    // <SiteHeader>/<SiteFooter>; `false` omits the slot entirely; `undefined`
    // (default) renders the site chrome (subject to the legacy iframe-mode
    // gate), so existing full-document pages are unaffected.
    header?: React.ReactNode | false;
    footer?: React.ReactNode | false;
    // Messages slot. `false` threads an empty list into the SiteHeader
    // (which renders the flash messages internally); pair with
    // build_page_chrome's include_messages=False so the flash queue is not
    // drained either. `undefined` (default) threads pageChrome.messages.
    messages?: false;
    // Fragment layout controls for the embeddable nav/footer iframe documents.
    // Header and footer omission use their slots (`header={false}` /
    // `footer={false}`); these remaining controls affect distinct behavior.
    //   wrapMain  === false   -> render children directly, with no
    //                            <main id="main-content"> wrapper.
    //   showOneTrust === false -> suppress the OneTrust consent furniture (the
    //                            SDK loader AND the OptanonWrapper stub). All
    //                            other tracking furniture is left intact.
    wrapMain?: boolean;
    showOneTrust?: boolean;
};

const UnsupportedWarning = ({
    staticBase,
}: {
    staticBase: string;
}): React.JSX.Element => {
    const browsers = [
        {
            title: t`Get Google Chrome`,
            href: "http://www.google.com/chrome",
            img: "tsicommon_cms/img/chrome_32x32.png",
            label: t`Google Chrome`,
        },
        {
            title: t`Get Mozilla Firefox`,
            href: "http://www.firefox.com",
            img: "tsicommon_cms/img/firefox_32x32.png",
            label: t`Mozilla Firefox`,
        },
        {
            title: t`Get Apple Safari`,
            href: "http://www.apple.com/safari/download",
            img: "tsicommon_cms/img/safari_32x32.png",
            label: t`Apple Safari`,
        },
        {
            title: t`Get Microsoft Edge`,
            href: "https://www.microsoft.com/en-us/edge",
            img: "tsicommon_cms/img/edge_32x32.png",
            label: t`Microsoft Edge`,
        },
    ];
    return (
        <>
            <div className="unsupported-browser-container">
                <div className="unsupported-browser">
                    <a
                        className="unsupported-browser__close-btn"
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            const container =
                                e.currentTarget.parentNode?.parentNode;
                            if (container instanceof HTMLElement) {
                                container.style.display = "none";
                            }
                        }}
                    >
                        {t`Close`}
                    </a>
                    <div className="unsupported-browser__title">
                        {t`Improve Your Shopping Experience`}
                    </div>
                    <p className="unsupported-browser__copy">
                        {t`You’re using an unsupported web browser.`}
                        <br />
                        {t`A list of the most popular browsers can be found below. Just click on an icon to visit the download page.`}
                    </p>
                    <ul className="unsupported-browser__list">
                        {browsers.map((b) => (
                            <li
                                className="unsupported-browser__list-item"
                                key={b.img}
                            >
                                <a
                                    title={b.title}
                                    href={b.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <img
                                        src={`${staticBase}${b.img}`}
                                        alt={b.title}
                                        title={b.title}
                                        loading="lazy"
                                        decoding="async"
                                    />
                                    <br />
                                    {b.label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
            <div className="unsupported-mobileos-container">
                <div className="unsupported-mobileos">
                    <a
                        className="unsupported-mobileos__close-btn"
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            const container =
                                e.currentTarget.parentNode?.parentNode;
                            if (container instanceof HTMLElement) {
                                container.style.display = "none";
                            }
                        }}
                    >
                        {t`Close`}
                    </a>
                    <div className="unsupported-mobileos__title">
                        {t`Your Software is Out of Date`}
                    </div>
                    <p className="unsupported-mobileos__copy">
                        {t`Your phone or tablet is using software unsupported by our website. To improve your shopping experience, please update your device to the latest version of iOS or Android OS.`}
                    </p>
                </div>
            </div>
        </>
    );
};

export const PageSkeleton = (props: IProps): React.JSX.Element => {
    const {
        pageChrome,
        children,
        header,
        footer,
        messages,
        wrapMain,
        showOneTrust,
    } = props;
    // Global chrome (header/nav, footer, head/body globals) comes from the
    // `chrome` context processor. PageSkeleton is always the full-document
    // root inside the reactivated Provider, so the context is populated in
    // both SSR and client hydration. This read is intentionally unguarded:
    // callers rendering outside a chrome-carrying Provider (stories/tests)
    // must supply a chrome fixture in their Context value.
    const context = React.useContext(Context);
    const chrome = context.chrome;
    // Post-hydration DOM decorators shared by every full-document page: fill CMS
    // rich-text promo tokens, apply the link-rewrite ruleset, and focus the
    // first form error.
    useRichTextDecorators([initPromoCalloutMessages]);
    useLinkRewrites();
    useFocusFirstFormError();
    // On a full-document React page React owns <html className="no-js"> (rendered
    // for the no-JS fallback) and `data-useragent` drives the unsupported-browser
    // CSS (html[data-useragent*="MSIE 10.0"], etc.). Both are per-user / JS-gated
    // and these pages are cacheable, so neither can be server-rendered, and an
    // early imperative mutation would race hydration and mismatch on the
    // React-owned <html>. Apply both here, after hydration commits: React owns
    // <html> but never carries `data-useragent` in its vdom and won't re-render
    // <html> (its props don't change), so removing `no-js` sticks. index.tsx
    // still drops `no-js` eagerly on island pages (no full-document hydration to
    // race); legacy.js still sets `data-useragent` for old browsers that never
    // boot React (see legacy.tsx's module-support guard).
    React.useEffect(() => {
        const win = getWindow();
        const doc = getDocument();
        if (doc?.documentElement) {
            doc.documentElement.classList.remove("no-js");
            if (win?.navigator) {
                doc.documentElement.setAttribute(
                    "data-useragent",
                    win.navigator.userAgent,
                );
            }
        }
    }, []);
    React.useEffect(() => {
        const remember = () =>
            rememberPageLanguage(context.request.path, chrome.html_lang);
        remember();
        // Pages restored from the back/forward cache don't remount.
        const onPageShow = (e: PageTransitionEvent) => {
            if (e.persisted) {
                remember();
            }
        };
        const win = getWindow();
        win?.addEventListener("pageshow", onPageShow);
        return () => win?.removeEventListener("pageshow", onPageShow);
    }, [context.request.path, chrome.html_lang]);
    // Configurator upsell modal offsets, written as CSS custom properties on
    // <html>: the panel offset tracks the top of #main-content (clamped to 0
    // once scrolled past it), recomputed on scroll; the overlay footer offset is
    // the fixed service-footer height, set only when the service footer is on
    // the page. Client-only (post-hydration) since they read the live DOM; the
    // properties fall back to their SCSS defaults until then.
    React.useEffect(() => {
        const win = getWindow();
        const doc = getDocument();
        if (!win || !doc) {
            return;
        }
        const root = doc.documentElement;
        const mainContent = doc.getElementById("main-content");
        const setUpsellPanelOffset = () => {
            if (!mainContent) {
                return;
            }
            const top = mainContent.getBoundingClientRect().top;
            root.style.setProperty(
                "--configurator--upsell--panel-offset",
                top < 0 ? "0px" : `${top}px`,
            );
        };
        if (doc.querySelector(".service-footer-container")) {
            root.style.setProperty(
                "--configurator--upsell--panel-overlay-footer-offset",
                "50px",
            );
        }
        setUpsellPanelOffset();
        // Coalesce scroll events into one rAF: `getBoundingClientRect()` forces
        // a synchronous layout, and scroll fires at high frequency, so calling
        // it per-event thrashes layout on long pages. `passive` lets the browser
        // skip waiting on a possible preventDefault.
        let ticking = false;
        const onScroll = () => {
            if (ticking) {
                return;
            }
            ticking = true;
            win.requestAnimationFrame(() => {
                setUpsellPanelOffset();
                ticking = false;
            });
        };
        win.addEventListener("scroll", onScroll, { passive: true });
        return () => {
            win.removeEventListener("scroll", onScroll);
        };
    }, []);
    const bodyClass = chrome.is_iframe_mode
        ? `${pageChrome.body_class} iframe-content`
        : pageChrome.body_class;
    // twitter:title / og:title use the bare seo title (no "| shop" suffix);
    // the shared PageChrome's seo_heading is the Track-B analogue.
    const socialTitle = pageChrome.seo_title_bare ?? pageChrome.seo_heading;
    // og:title / og:description are independently overridable per page,
    // unlike twitter:title / twitter:description, so pages overriding them
    // carry separate og values (null = no override).
    const ogTitle = pageChrome.og_title ?? socialTitle;
    const twitterDescription =
        pageChrome.twitter_description ?? pageChrome.search_description;
    // twitter:url / og:url; the shared PageChrome carries current_url instead.
    const socialUrl = pageChrome.og_url ?? pageChrome.current_url;
    // The SiteHeader renders the flash messages internally; thread the
    // per-page queue into it (an empty list still renders the empty
    // <aside class="messages"> wrapper), adapting the shared Track-B
    // MessageItem shape to the TP Message shape it eats.
    const headerMessages =
        messages === false ? [] : adaptHeaderMessages(pageChrome.messages);
    return (
        <html className="no-js" lang={chrome.html_lang}>
            <head>
                {/* FOUC inline-style omitted: React 19 hoists the bundle CSS to
                    the top of <head>, which would invert the visibility-guard
                    ordering. The render-blocking bundle <link>
                    means no flash to guard. */}
                <title>{pageChrome.title}</title>
                {!chrome.do_not_track && chrome.optimizely_project_id && (
                    <script
                        src={`https://cdn.optimizely.com/js/${chrome.optimizely_project_id}.js`}
                        async={true}
                    />
                )}
                <meta
                    httpEquiv="content-type"
                    content="text/html; charset=UTF-8"
                />
                <meta name="created" content={chrome.created} />
                <meta
                    name="viewport"
                    content="width=device-width, initial-scale=1.0, viewport-fit=cover"
                />
                <meta
                    name="google-site-verification"
                    content={chrome.google_site_verification}
                />
                <link rel="preconnect" href={chrome.favicon_preconnect_url} />
                {pageChrome.search_description && (
                    <meta
                        name="description"
                        content={pageChrome.search_description}
                    />
                )}
                {pageChrome.search_keywords && (
                    <meta
                        name="keywords"
                        content={pageChrome.search_keywords}
                    />
                )}
                {pageChrome.robots_noindex && (
                    <meta name="robots" content="noindex" />
                )}
                <link rel="canonical" href={pageChrome.canonical_url} />
                {(pageChrome.hreflang_alternates ?? []).map((alt, i) => (
                    <link
                        key={i}
                        rel="alternate"
                        href={alt.href}
                        hrefLang={alt.hreflang}
                    />
                ))}
                <meta name="twitter:card" content="summary" />
                <meta name="twitter:site" content="@TempurPedic" />
                <meta name="twitter:title" content={socialTitle} />
                <meta name="twitter:description" content={twitterDescription} />
                <meta name="twitter:url" content={socialUrl} />
                <meta property="og:title" content={ogTitle} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={socialUrl} />
                <meta
                    property="og:description"
                    content={pageChrome.search_description}
                />
                <meta
                    property="og:site_name"
                    content={
                        chrome.app_slug === "sale2"
                            ? "Tempur-Pedic® Outlet"
                            : "Tempur-Pedic"
                    }
                />
                {/* Per-page social-share images; no tag is rendered when empty. */}
                {pageChrome.twitter_image && (
                    <meta
                        name="twitter:image"
                        content={pageChrome.twitter_image}
                    />
                )}
                {pageChrome.og_image && (
                    <meta property="og:image" content={pageChrome.og_image} />
                )}
                {/* sale2 WebSite JSON-LD + page JSON-LD render at body-top (with
                    the other inline scripts), not here -- crawlers read JSON-LD
                    anywhere in the document. */}
                <link
                    rel="icon"
                    type="image/x-icon"
                    href={chrome.favicon_url}
                />
                {chrome.skin_css_urls.map((url, i) => (
                    <link key={i} rel="stylesheet" type="text/css" href={url} />
                ))}
                {pageChrome.extra_stylesheets.map((url, i) => (
                    <link
                        key={`page-css-${i}`}
                        rel="stylesheet"
                        type="text/css"
                        href={url}
                    />
                ))}
                <link
                    rel="stylesheet"
                    type="text/css"
                    href="//fonts.googleapis.com/css?family=Open+Sans"
                />
                {chrome.aws_waf_url && (
                    <script defer={true} src={chrome.aws_waf_url} />
                )}
            </head>
            <body
                id={chrome.body_id}
                data-appslug={chrome.app_slug}
                data-version={chrome.version}
                data-environment={chrome.environment}
                data-sentry-dsn={chrome.sentry_dsn}
                data-track-event={pageChrome.event_name ?? ""}
                data-track-event-data={pageChrome.event_data ?? ""}
                data-track-page-type={pageChrome.page_type}
                data-view-name={chrome.view_name}
                className={bodyClass}
            >
                {/*
                 * The consent stub, the third-party tags and the JSON-LD render
                 * at the TOP of <body>, not in <head>. React 19 hoists the
                 * <meta> and async <script src> tags up anyway, while an inline
                 * <script> in the React-owned <head> gets paired with whatever a
                 * third party injects there before hydration finishes -- shifting
                 * the whole head by one. OptanonWrapper only has to exist before
                 * the async otSDKStub calls it, GTM events queue in dataLayer,
                 * and JSON-LD is crawled anywhere.
                 */}
                {showOneTrust !== false && (
                    <script
                        type="text/javascript"
                        dangerouslySetInnerHTML={{
                            __html: "function OptanonWrapper() { }",
                        }}
                    />
                )}
                <MetaTags
                    tags={context.meta_tags.meta_tags.value}
                    doNotTrack={chrome.do_not_track}
                    showOneTrust={showOneTrust}
                />
                {chrome.sale2_json_ld && (
                    <script
                        type="application/ld+json"
                        dangerouslySetInnerHTML={{
                            __html: chrome.sale2_json_ld,
                        }}
                    />
                )}
                {pageChrome.json_ld.map((schema, i) => (
                    <script
                        key={i}
                        type="application/ld+json"
                        dangerouslySetInnerHTML={{ __html: schema }}
                    />
                ))}
                {/* #root: required for react-modal (index.ts). */}
                <div id="root">
                    {/* The sale2 deployment renders a structurally different
                        header (outlet logo, countdown-timer stripe,
                        value-props bar); chrome carries its
                        props only on that APP_SLUG. */}
                    {header === undefined
                        ? !chrome.is_iframe_mode &&
                          (chrome.sale2_site_header ? (
                              <Sale2SiteHeader
                                  {...chrome.sale2_site_header}
                                  messages={headerMessages}
                              />
                          ) : (
                              <SiteHeader
                                  {...chrome.site_header}
                                  messages={headerMessages}
                                  hreflangAlternates={
                                      pageChrome.hreflang_alternates ?? []
                                  }
                              />
                          ))
                        : header}
                    {wrapMain !== false ? (
                        <main id="main-content">
                            {/* Contain a client-render throw to the page body:
                                without a boundary a crash blanks the WHOLE
                                document (header/footer included) post-hydration. */}
                            <ErrorBoundary>{children}</ErrorBoundary>
                        </main>
                    ) : (
                        // The nav/footer iframe fragments render their
                        // iframe-resizer scripts directly here, with no <main>
                        // wrapper.
                        children
                    )}
                    {footer === undefined
                        ? !chrome.is_iframe_mode && (
                              <SiteFooter footer={chrome.footer} />
                          )
                        : footer}
                </div>
                {/* admintools (wagtailuserbar) is a Django admin-only tool
                    (CSRSettings.show_admin_tools, off in the public render) --
                    omitted here. */}
                <SessionKeepAlive />
                <FinancingModal />
                {/* DNT-gated Google Customer Reviews badge. */}
                {!chrome.do_not_track && (
                    <script
                        src="https://apis.google.com/js/platform.js?onload=renderBadge"
                        async
                        defer
                    />
                )}
                <UnsupportedWarning staticBase={chrome.static_base} />
                {/*
                 * `async` (not `defer`) so React 19 hoists this <script src> to
                 * <head> as a managed resource instead of hydrating it
                 * positionally at body-end. tempur.html's GTM container injects
                 * Custom HTML tags as <body>/<head> siblings during load; a
                 * positional body-end <script> vnode races React's hydration of
                 * that node and, when the GTM injection wins, React pairs its
                 * legacy.js vnode against the foreign <script> and throws a FATAL
                 * "tree will be regenerated" mismatch. Hoisting removes the
                 * body-end script vnode entirely, so the foreign injection has
                 * nothing to collide with; React 19 skips foreign body/head
                 * <script> siblings cleanly. legacy.tsx is order-independent (it
                 * only sets data-useragent for old non-module browsers, which
                 * never boot React anyway), so async is semantically safe and old
                 * browsers still receive it from the SSR <head>.
                 */}
                <script
                    async={true}
                    src={`${chrome.static_base}dist2/legacy.js`}
                />
                {/* Load the Zendesk chat widget only when global chat is
                    enabled (GlobalChrome.zendesk_chat_enabled =
                    ChatSettings.chat_enabled) AND the page opted in
                    (PageChrome.chat_widget_enabled) and a widget key is
                    configured. */}
                {chrome.zendesk_chat_enabled &&
                    pageChrome.chat_widget_enabled &&
                    chrome.zendesk_key && (
                        <script
                            id="ze-snippet"
                            src={`https://static.zdassets.com/ekr/snippet.js?key=${chrome.zendesk_key}`}
                        />
                    )}
            </body>
        </html>
    );
};
